import { DeleteIcon, EditIcon } from '@/components/_common';
import {
  useStoneDeviceList,
  useStoneDeviceRemove,
} from '@/core/domain/stone/devices/devices.hooks';
import { StoneDevice } from '@/core/domain/stone/devices/devices.types';
import {
  Button,
  Col,
  Modal,
  Row,
  Space,
  Table,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import StoneDeviceModal from '../StoneDeviceModal';
import { useState } from 'react';
import { useDisclosure } from '@/hooks';
import { StoneAccount } from '@/core/domain/stone/stoneAccount/stoneAccount.types';
import StatusTag from '@/components/_common/StatusTag';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';

interface Props {
  stoneAccount?: StoneAccount;
}

export default function StoneDeviceList(props: Props) {
  const stoneDeviceList = useStoneDeviceList({
    companyId: props.stoneAccount?.companyId,
  });
  const stoneDeviceRemove = useStoneDeviceRemove();
  const isActive = !!props.stoneAccount?.active;

  const [deviceSelected, setDeviceSelected] = useState<StoneDevice>();
  const [modalOpened, modalAction] = useDisclosure(false, {
    onClose: () => setDeviceSelected(undefined),
  });

  function confirmRemove(device: StoneDevice) {
    Modal.confirm({
      title: 'Remoção de dispositivo',
      content: `Tem certeza que deseja remover o dispositivo "${device.serialNumber}"?`,
      okText: 'Remover',
      onOk: () => stoneDeviceRemove.mutateAsync({ deviceId: device.id }),
      okType: 'danger',
    });
  }

  const columns: ColumnsType<StoneDevice> = [
    {
      title: 'Número do terminal',
      dataIndex: 'serialNumber',
      render: (value) => value || '-',
    },
    {
      title: 'Situação',
      dataIndex: 'active',
      render(active) {
        return (
          <>
            {active && <StatusTag color="green" label="Ativado" />}
            {!active && <StatusTag color="default" label="Desativado" />}
          </>
        );
      },
    },
    {
      title: 'Ação',
      align: 'right',
      render: (device) => (
        <Space size={16}>
          <EditIcon
            iconProps={{
              onClick: () => {
                setDeviceSelected(device);
                modalAction.open();
              },
            }}
            disabled={!hasPermissions([Authorities.STONE_DEVICE_EDIT])}
          />

          <DeleteIcon
            onClick={() => confirmRemove(device)}
            disabled={!hasPermissions([Authorities.STONE_DEVICE_DELETE])}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row style={{ marginBottom: 24 }} gutter={[24, 16]}>
        <Col flex={1}>
          <Typography.Title style={{ marginBottom: 24 }} level={4}>
            Dispositivos stone
          </Typography.Title>
        </Col>

        {hasPermissions([Authorities.STONE_DEVICE_CREATE]) && (
          <Col>
            <Tooltip title={!isActive ? 'Conta Stone desativada' : undefined}>
              <Button
                type="primary"
                onClick={modalAction.open}
                disabled={!isActive}
              >
                Cadastrar dispositivo
              </Button>
            </Tooltip>
          </Col>
        )}
      </Row>

      <Table
        dataSource={stoneDeviceList.data}
        loading={stoneDeviceList.isLoading}
        columns={columns}
        scroll={{ x: true }}
        pagination={false}
      />

      <StoneDeviceModal
        opened={modalOpened}
        onClose={modalAction.close}
        device={deviceSelected}
        stoneAccount={props.stoneAccount}
      />
    </>
  );
}
